import { apiCall, ApiResult } from './core';

export const projectAPI = {
  getProjects: async (userRole: string, _tenantId: string | null, _projectId: string | null, clientId: string | null = null): Promise<ApiResult> => {
    try {
      let endpoint = '/projects';
      // For AWS users, client_id is required
      if (userRole === 'aws') {
        if (!clientId) {
          return { success: true, data: [] };
        }
        endpoint += `?client_id=${clientId}`;
      } else if (clientId) {
        endpoint += `?client_id=${clientId}`;
      }
      const data = await apiCall(endpoint);
      return { success: true, data: data || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error fetching projects:', error);
      return { success: false, error: err.message };
    }
  },

  getProject: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  updateProject: async (projectId: string, updates: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  updateProjectStatus: async (projectId: string, status: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  createProject: async (projectData: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall('/projects', {
        method: 'POST',
        body: JSON.stringify(projectData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Client API methods
  getClients: async (): Promise<ApiResult> => {
    try {
      const data = await apiCall('/clients');
      // API returns array directly, not wrapped in { clients: [] }
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  createClient: async (clientData: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall('/clients', {
        method: 'POST',
        body: JSON.stringify(clientData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // People/Employee management methods (delegating to peopleAPI for consistency)
  getPartnerEmployees: async (): Promise<ApiResult> => {
    try {
      const data = await apiCall('/employees');
      return { success: true, data: data.employees || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getProjectPeople: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people`);
      return { success: true, data: data.people || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  addProjectPerson: async (projectId: string, personData: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people`, {
        method: 'POST',
        body: JSON.stringify(personData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  updateProjectPerson: async (projectId: string, personId: string, updates: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people/${personId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  removeProjectPerson: async (projectId: string, personId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/people/${personId}`, {
        method: 'DELETE'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  deleteProject: async (projectId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/projects/${projectId}`, {
        method: 'DELETE'
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  exportProject: async (projectId: string): Promise<ApiResult> => {
    try {
      // TODO: Implement actual export endpoint when backend is ready
      // For now, return project data as JSON
      const data = await apiCall(`/projects/${projectId}`);

      // Create a downloadable JSON file
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `project-${projectId}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Project AWS Environment linking
  getProjectEnvironments: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/environments`);
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  linkProjectEnvironment: async (projectId: string, environmentId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/projects/${projectId}/environments`, {
        method: 'POST',
        body: JSON.stringify({ environment_id: environmentId })
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  unlinkProjectEnvironment: async (projectId: string, environmentId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/projects/${projectId}/environments/${environmentId}`, {
        method: 'DELETE'
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  updateCostConfig: async (projectId: string, config: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/cost-config`, {
        method: 'PUT',
        body: JSON.stringify(config)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Statement of Work (SoW) operations
  getSoWUploadUrl: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/sow/upload-url`, {
        method: 'POST'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  confirmSoWUpload: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/sow/confirm`, {
        method: 'POST'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getSoWDownloadUrl: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/sow/download-url`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  deleteSoW: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/sow`, {
        method: 'DELETE'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Google Drive link operations
  setGoogleDriveLink: async (projectId: string, googleDriveUrl: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/sow/google-drive`, {
        method: 'POST',
        body: JSON.stringify({ google_drive_url: googleDriveUrl })
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  removeGoogleDriveLink: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/sow/google-drive`, {
        method: 'DELETE'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getGoogleDriveFiles: async (projectId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/projects/${projectId}/sow/google-drive/files`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  }
};
